import { type ReactNode } from 'react'
import { useScroll, useTransform, type MotionValue } from 'motion/react'
import { cn } from '@/lib/utils'
import FTC from '@/components/widgets/ftc/FTC'
import Sparkles from '@/components/ui/Sparkles'

type HeroRenderProps = {
  animations?: {
    opacity?: MotionValue<number>
    y?: MotionValue<number>
    scale?: MotionValue<number>
  }
}

type HeroProps = {
  gradient?: string
  height?: {
    auto?: boolean
    value?: number
    expanded?: boolean
    condensed?: boolean
  }
  animation?: {
    opacity?: boolean
    scroll?: boolean
    scale?: boolean
  }
  center?: boolean
  ftc?: boolean
  children: (props: HeroRenderProps) => ReactNode
  className?: string
}

export default function Hero({
  gradient,
  height,
  animation,
  center = true,
  ftc = false,
  children,
  className,
}: HeroProps) {
  const { scrollY } = useScroll()

  const {
    opacity: opacityEnabled,
    scroll: scrollEnabled,
    scale: scaleEnabled,
  } = {
    opacity: animation?.opacity ?? true,
    scroll: animation?.scroll ?? true,
    scale: animation?.scale ?? false,
  }

  const opacityTransform = useTransform(scrollY, [0, height?.value ?? 560], [1, 0])
  const scrollTransform = useTransform(scrollY, [0, height?.value ?? 560], [0, -50])
  const scaleTransform = useTransform(scrollY, [0, height?.value ?? 560], [1, 0.8])

  const opacity = opacityEnabled ? opacityTransform : undefined
  const scroll = scrollEnabled ? scrollTransform : undefined
  const scale = scaleEnabled ? scaleTransform : undefined

  const hasAnyAnimation = opacity || scroll || scale
  // Previously gated on `isMobile` from useBreakpoint. That returns false on
  // SSR and the real value at hydration, so the `style` prop's object identity
  // flipped on every desktop page load → forced a wasted subtree re-render of
  // the entire Hero. The motion values are opacity/transform-only (don't
  // reflow), so dropping the gate has no layout cost. If we ever need to
  // actually skip scroll-listener work on mobile for perf, do it inside the
  // useScroll/useTransform setup, not at the render level.
  const animations = hasAnyAnimation ? { opacity, y: scroll, scale } : undefined

  return (
    <section
      className={cn(
        'z-10 flex w-screen text-white transition-[height]',
        height?.auto ? 'relative' : 'absolute top-0 overflow-hidden md:fixed',
        height?.condensed
          ? 'h-hero-condensed md:h-hero'
          : height?.expanded
            ? 'h-hero md:h-hero-expanded'
            : height?.auto
              ? 'h-auto'
              : 'h-hero',
        !height?.expanded && !height?.auto && 'md:tall:h-hero-expanded',
        center && 'md:items-center',
        className,
      )}
      style={{ background: gradient }}
    >
      {children({ animations })}
      {ftc && <FTC />}
      <Sparkles />
    </section>
  )
}
